import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import axiosInstance from '../../../helpers/axios';
import { formatCurrency } from '../utils/format';
import { fibeTokens } from '../../../theme';
import PaginationTable from '../../status/PaginationTable';

const DPD_HOLD_THRESHOLD = 30;
const PAGE_SIZE = 15;

function RefreshGlyph() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v5h-5" />
        </svg>
    );
}

function WarningGlyph() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
    );
}

function CheckGlyph() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', marginTop: 1 }}>
            <path d="M20 6 9 17l-5-5" />
        </svg>
    );
}

function ErrorBanner({ title, message, onRetry, retryLabel = 'Retry' }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '14px',
                background: fibeTokens.color.dangerTint,
                border: `1px solid ${fibeTokens.color.dangerBorder}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                padding: fibeTokens.density.cardPadding,
            }}
        >
            <Box>
                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.danger }}>
                    {title}
                </Box>
                <Box sx={{ ...fibeTokens.font.helper, fontFamily: fibeTokens.font.family, color: fibeTokens.color.danger, marginTop: '4px' }}>
                    {message}
                </Box>
            </Box>
            {onRetry && (
                <Box sx={{ flex: 'none' }}>
                    <Button variant="fibeSecondary" size="small" onClick={onRetry}>{retryLabel}</Button>
                </Box>
            )}
        </Box>
    );
}

function UploadSuccessBanner({ result }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginTop: '14px',
                padding: '12px 14px',
                background: fibeTokens.color.successTint,
                border: `1px solid ${fibeTokens.dialog.icon.green.border}`,
                borderRadius: '10px',
                color: fibeTokens.color.success,
            }}
        >
            <CheckGlyph />
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12.5, lineHeight: 1.5, color: fibeTokens.color.success }}>
                Upload complete — <Box component="strong" sx={{ fontWeight: 700 }}>{result.totalRows} rows</Box> processed, {result.matched} matched,{' '}
                {result.newlyHeld} newly held, {result.stillHeld} still held, {result.released} released, {result.skipped} skipped.
            </Box>
        </Box>
    );
}

export function UploadDpdForm({ onUploaded }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const handleUpload = () => {
        if (!selectedFile) {
            setUploadError('Choose a file first.');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);

        setIsUploading(true);
        setUploadError(null);

        axiosInstance
            .post('/fibe-billing/dpd/upload', formData)
            .then((response) => {
                setUploadResult(response.data.data);
                setSelectedFile(null);
                setFileInputKey((key) => key + 1);
                onUploaded();
            })
            .catch((error) => setUploadError(error.response?.data?.message ?? 'Upload failed.'))
            .finally(() => setIsUploading(false));
    };

    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: fibeTokens.density.cardPadding,
                marginBottom: '14px',
            }}
        >
            <Box component="h2" sx={{ margin: '0 0 14px', fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                Upload Monthly DPD File from Fibe
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    border: `1.5px dashed ${fibeTokens.color.borderStrong}`,
                    borderRadius: '12px',
                    background: fibeTokens.color.pageBg,
                    padding: '24px',
                }}
            >
                <Box component="label" htmlFor="dpd-file-input" sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0, cursor: 'pointer' }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            flex: 'none',
                            borderRadius: '11px',
                            background: fibeTokens.color.dangerIconTint,
                            color: fibeTokens.color.danger,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <WarningGlyph />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13.5, color: fibeTokens.color.textSecondary }}>
                            Drop Fibe's defaulter report, or <Box component="span" sx={{ color: fibeTokens.color.primary }}>browse</Box>
                        </Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper, marginTop: '4px' }}>
                            Max 10 MB · .csv, .xlsx · contains only DPD &gt; 0 loans
                        </Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper, marginTop: '4px' }}>
                            {selectedFile ? selectedFile.name : 'No file chosen'}
                        </Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 500, fontSize: 11.5, color: fibeTokens.color.warning, marginTop: '3px' }}>
                            Loans not in this file retain their previous DPD status
                        </Box>
                    </Box>
                </Box>
                <input
                    id="dpd-file-input"
                    key={fileInputKey}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setSelectedFile(e.target.files[0] ?? null)}
                    style={{ display: 'none' }}
                />
                <Button variant="fibePrimary" onClick={handleUpload} disabled={isUploading} sx={{ flex: 'none' }}>
                    {isUploading ? 'Uploading...' : 'Upload & Apply'}
                </Button>
            </Box>

            {uploadError && (
                <Box sx={{ marginTop: '14px' }}>
                    <ErrorBanner title="Could not upload file" message={uploadError} />
                </Box>
            )}
            {uploadResult && <UploadSuccessBanner result={uploadResult} />}
        </Box>
    );
}

function DpdEmptyState() {
    return (
        <Box
            sx={{
                textAlign: 'center',
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: '40px 18px',
            }}
        >
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    margin: '0 auto 12px',
                    borderRadius: '12px',
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: fibeTokens.color.textDisabled,
                }}
            >
                <WarningGlyph />
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.textPrimary }}>
                No DPD file has been uploaded yet.
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12, lineHeight: 1.6, color: fibeTokens.color.textMuted, margin: '5px 0 0' }}>
                Upload a DPD file above to see results here.
            </Box>
        </Box>
    );
}

const DPD_SKELETON_COLUMNS = [
    { width: 78 },
    { width: 82 },
    { width: 40 },
    { width: 40 },
    { width: 70 },
    { width: 70 },
    { width: 96, pill: true },
];

function DpdResultsSkeleton() {
    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}` }}>
                <Skeleton variant="text" width={200} height={16} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', padding: '16px 18px 14px' }}>
                {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={58} sx={{ borderRadius: '10px' }} />
                ))}
            </Box>
            {Array.from({ length: 5 }).map((_, rowIndex) => (
                <Box
                    key={rowIndex}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        height: `${fibeTokens.density.tableRowHeight}px`,
                        padding: '0 14px',
                        borderBottom: rowIndex < 4 ? `1px solid ${fibeTokens.color.border}` : 'none',
                    }}
                >
                    {DPD_SKELETON_COLUMNS.map((col, colIndex) => (
                        <Skeleton
                            key={colIndex}
                            variant={col.pill ? 'rounded' : 'text'}
                            width={col.width}
                            height={col.pill ? 18 : 12}
                            sx={{ borderRadius: col.pill ? '999px' : undefined, flex: 'none' }}
                        />
                    ))}
                </Box>
            ))}
        </Box>
    );
}

function PageHeader({ isLoading, onRefresh }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <Box>
                <Box component="h1" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.pageTitle, color: fibeTokens.color.textPrimary }}>
                    DPD Upload &amp; Tracking
                </Box>
                <Box sx={{ margin: '5px 0 0', fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper }}>
                    Milestones are held automatically at 30+ days past due
                </Box>
            </Box>
            <Button
                variant="fibeSecondary"
                disabled={isLoading}
                onClick={onRefresh}
                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshGlyph />}
            >
                {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
        </Box>
    );
}

function formatUploadDate(value) {
    return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function DpdTab() {
    const [latestBatch, setLatestBatch] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [page, setPage] = useState(1);

    const loadLatestBatch = useCallback((pageNo) => {
        setIsLoading(true);
        setErrorMessage(null);

        axiosInstance
            .get('/fibe-billing/dpd/latest', { params: { pageNo, perPage: PAGE_SIZE } })
            .then((response) => setLatestBatch(response.data.data))
            .catch(() => setErrorMessage('Could not load the latest DPD upload.'))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadLatestBatch(page);
    }, [page, loadLatestBatch]);

    const handleUploaded = () => {
        setPage(1);
        loadLatestBatch(1);
    };

    return (
        <div>
            <PageHeader isLoading={isLoading} onRefresh={() => loadLatestBatch(page)} />

            <UploadDpdForm onUploaded={handleUploaded} />

            {isLoading ? (
                <DpdResultsSkeleton />
            ) : errorMessage ? (
                <ErrorBanner title="Could not load the latest DPD upload" message={errorMessage} onRetry={() => loadLatestBatch(page)} />
            ) : !latestBatch ? (
                <DpdEmptyState />
            ) : (
                <Box
                    sx={{
                        background: fibeTokens.color.surface,
                        border: `1px solid ${fibeTokens.color.border}`,
                        borderRadius: `${fibeTokens.density.cardRadius}px`,
                        boxShadow: fibeTokens.density.cardShadow,
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}` }}>
                        <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                            Last DPD Upload — {formatUploadDate(latestBatch.uploadDate)}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', padding: '16px 18px 14px' }}>
                        {[
                            { key: 'rowCount', label: 'Rows Matched', value: latestBatch.rowCount },
                            { key: 'heldCount', label: 'Currently Held', value: latestBatch.heldCount, tone: fibeTokens.color.danger },
                            { key: 'notHeldCount', label: 'Not Held (DPD < 30)', value: latestBatch.notHeldCount, tone: fibeTokens.color.success },
                        ].map((stat) => (
                            <Box
                                key={stat.key}
                                sx={{ border: `1px solid ${fibeTokens.color.border}`, borderRadius: '10px', padding: '11px 13px', background: fibeTokens.color.rowHover }}
                            >
                                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 10, color: fibeTokens.color.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    {stat.label}
                                </Box>
                                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 18, color: stat.tone ?? fibeTokens.color.textPrimary, marginTop: '5px', letterSpacing: '-0.015em' }}>
                                    {stat.value}
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ overflowX: 'auto' }}>
                        <Table sx={{ width: '100%', minWidth: '760px', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 130 }}>App ID</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 130 }}>Fibe Loan ID</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 100 }}>Current DPD</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 100 }}>EMIs Paid</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 120 }}>Overdue Amt</TableCell>
                                    <TableCell size="fibe" variant="head" align="left">Due Month</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 150 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {latestBatch.records.map((record) => {
                                    const isHeld = record.dpdValue >= DPD_HOLD_THRESHOLD;
                                    return (
                                        <TableRow key={record.id} hover>
                                            <TableCell size="fibe" variant="body" align="left">
                                                {record.applicationId}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="left">
                                                {record.fibeLoanId ?? '—'}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontWeight: 700, color: isHeld ? fibeTokens.color.danger : fibeTokens.color.success, fontVariantNumeric: 'tabular-nums' }}>
                                                {record.dpdValue}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {record.emisPaid ?? '—'}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {record.overdue != null ? formatCurrency(record.overdue) : '—'}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="left">
                                                {record.dueMonth ?? '—'}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="left">
                                                <Chip variant="fibePill" color={isHeld ? 'error' : 'success'} label={isHeld ? 'Held' : 'DPD < 30 (Not Held)'} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            )}

            {latestBatch && (
                <Box sx={{ marginTop: '14px' }}>
                    <PaginationTable
                        key={latestBatch.uploadBatchId}
                        startPage={page}
                        rowsPerPage={latestBatch.pagination?.perPage ?? PAGE_SIZE}
                        totalRows={latestBatch.rowCount}
                        paginate={setPage}
                    />
                </Box>
            )}
        </div>
    );
}

export default DpdTab;
